/**
 * DOCUMENTATION:
 * Component: CleanCodeFunctions
 * Purpose: Teaching the Single Responsibility Principle (SRP) and best practices for writing functions.
 * * * Note for Junior Developers:
 * - Rule 1: Functions should be small.
 * - Rule 2: Functions should do exactly one thing (Single Responsibility).
 * - Rule 3: Use the Stepdown Rule. The code should read like a top-down narrative,
 * with high-level functions at the top and implementation details at the bottom.
 * - Rule 4: Limit arguments. If a function needs more than 3 arguments, it likely
 * needs to be passed an object instead.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
    },
}));

/* ✅ Main Component */
export default function CleanCodeFunctions() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const functionRules = useMemo(() => [
        { rule: "Single Responsibility (SRP)", description: "A function should have one reason to change. If it fetches data, calculates a price, and updates the UI, it's doing too much." },
        { rule: "Niladic / Monadic focus", description: "Aim for 0 to 1 arguments. If you hit 3+, pass an object. This makes the function call much easier to read." },
        { rule: "No Side Effects", description: "Avoid hidden changes. A function shouldn't secretly modify a global variable or change the state of an unrelated component." },
        { rule: "Command Query Separation", description: "A function should either DO something (Command) or ANSWER something (Query), but never both." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. SRP: Decomposing the 'God Function'",
            code: `// ❌ JUNIOR: One function handles validation, calculation, and UI alerts\nfunction processOrder(items) {\n  if (items.length === 0) {\n    alert('Cart is empty');\n    return;\n  }\n  let total = 0;\n  items.forEach(i => total += i.price);\n  const tax = total * 0.15;\n  console.log('Final:', total + tax);\n}\n\n// ✅ SENIOR: Separated concerns\nconst calculateTotal = (items) => items.reduce((sum, i) => sum + i.price, 0);\nconst calculateTax = (amount) => amount * 0.15;\n\nfunction processOrder(items) {\n  if (isCartEmpty(items)) return notifyUser('Cart is empty');\n  \n  const subtotal = calculateTotal(items);\n  const total = subtotal + calculateTax(subtotal);\n  \n  logOrderCompletion(total);\n}`,
            desc: "By splitting the logic, the 'calculateTotal' function becomes reusable and testable in isolation. If your tax logic changes, you only touch one small function."
        },
        {
            title: "2. Argument Mastery: Use Objects",
            code: `// ❌ BAD: What do these strings and booleans even represent?\ncreateUser('Kofi', 'Boateng', 25, true, 'Admin');\n\n// ✅ GOOD: Self-documenting arguments\ncreateUser({\n  firstName: 'Kofi',\n  lastName: 'Boateng',\n  age: 25,\n  isActive: true,\n  role: 'Admin'\n});`,
            desc: "When a function takes an object, the 'call-site' becomes readable. You don't have to check the function definition to know what the 4th boolean parameter does."
        },
        {
            title: "3. Pure Functions: No Side Effects",
            code: `// ❌ BAD: Secretly modifies an external variable\nlet globalScore = 0;\nfunction addToScore(points) {\n  globalScore += points;\n}\n\n// ✅ GOOD: Returns a new value, leaves the world unchanged\nfunction calculateNewScore(currentScore, points) {\n  return currentScore + points;\n}`,
            desc: "Pure functions are the foundation of clean React code. They are predictable: same input always results in the same output, and nothing else changes."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    FUNCTION MASTERY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            The Art of Smallness
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A function should be like a specialized tool in a workshop. A hammer doesn't try to be a screwdriver. In Clean Code, your functions should be so small that their purpose is undeniable.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Function Engineering Rules</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {functionRules.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.rule}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem" }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#e0b200" },
}));
